import { type Channel, type OutcomeCounts, type Problem, serviceLabel } from "@pagerless/schema";
import { Fragment, useState } from "react";
import { Link } from "react-router";
import type { FeedItem, Home as HomeData, ProblemView, Project } from "../api";
import { ScreenHead } from "../components/ScreenHead";
import { ErrorNote, Loading, useShell } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { clock, dayClock, greeting, lcdDuration, shortClock } from "../lib/format";
import {
  alertsPager,
  appsPager,
  CHANNEL_NAME,
  HOST_NAME,
  homePager,
  LOADING_PAGER,
  peoplePager,
  planPager,
  problemsPager,
  weeklyPager,
} from "../lib/pager";
import { Jobs } from "./Jobs";
import { AskBox } from "./Problem";
import { appHref } from "./Services";
import { defaultLevels, PeopleList } from "./Setup";

/** The week's counts, with one meaning everywhere (bug #18). Older servers only send fixed/needed. */
export function weekCounts(h: HomeData): OutcomeCounts {
  const fixedByHelper = h.stats.fixedByHelper ?? h.stats.fixed;
  return {
    fixedByHelper,
    neededYou: h.stats.neededYou ?? h.stats.needed,
    resolved: h.stats.resolved ?? fixedByHelper,
  };
}

export function Home() {
  const { now } = useDeps();
  const me = useResource((a) => a.me());
  const home = useResource((a) => a.home());
  const all = useResource((a) => a.problems());
  const { refreshBadge } = useShell();
  // Who fixed what today comes from each fix log: a hand close counts for nobody (C1).
  const startOfDay = new Date(now()).setHours(0, 0, 0, 0);
  const closedToday =
    all.status === "ok"
      ? all.data.filter((p) => p.state === "resolved" && (p.resolvedAt ?? 0) >= startOfDay).map((p) => p.id)
      : [];
  const todayViews = useResource(
    (a) => Promise.all(closedToday.map((id) => a.problem(id))),
    `${all.status}:${closedToday.join(",")}`,
  );

  if (home.status === "error") return <ErrorNote error={home.error} retry={home.reload} />;
  if (all.status === "error") return <ErrorNote error={all.error} retry={all.reload} />;
  if (todayViews.status === "error") return <ErrorNote error={todayViews.error} retry={todayViews.reload} />;
  // Never show a number or a greeting that isn't known yet (D1): wait for the name, the problems and
  // today's fix logs, then render once.
  if (home.status !== "ok" || me.status === "loading" || all.status !== "ok" || todayViews.status !== "ok")
    return <Loading what="Loading your week" />;
  const h = home.data;
  const week = weekCounts(h);
  const problems = all.status === "ok" ? all.data : [];
  const needsYou = problems.filter((p) => p.state === "open");
  const inProgress = problems.filter((p) => p.state === "fixing" || p.state === "acked").length;
  const name = me.status === "ok" ? me.data.person.name : "";
  const first = needsYou[0];

  const openIds = new Set(needsYou.map((p) => p.id));
  // Rows show when a problem started, as Problems does; the feed's own time is when it was last seen (bug 3).
  const openedAt = new Map(problems.map((p) => [p.id, p.openedAt]));
  const total = week.resolved + h.stats.told;

  return (
    <>
      <title>Home · Pagerless</title>
      <ScreenHead
        pager={homePager({
          needsYou: needsYou.length,
          inProgress,
          ...whoFixedToday(todayViews.data),
        })}
        title={`${greeting(now())}${name ? `, ${name}` : ""}`}
        sub={h.upDays ? `No outage in ${h.upDays} day${h.upDays === 1 ? "" : "s"}` : undefined}
      />
      {first && (
        <NeedsYouCard
          problem={first}
          more={needsYou.length - 1}
          onDone={() => {
            all.reload();
            home.reload();
            refreshBadge();
          }}
        />
      )}
      <h2 className="h3">This week{total > 0 ? ` · ${total} ${total === 1 ? "problem" : "problems"}` : ""}</h2>
      <section className="stats" aria-label="This week">
        <div className="box">
          <span className="n">{week.fixedByHelper}</span>
          <span>fixed by your helper</span>
        </div>
        <div className="box">
          <span className="n">{h.stats.told}</span>
          <span>told you, no action</span>
        </div>
        <div className="box">
          <span className="n">{week.neededYou}</span>
          <span>needed you</span>
        </div>
      </section>
      {h.feed.length === 0 ? (
        <p className="muted">Nothing yet.</p>
      ) : (
        groupFeed(h.feed, openIds, openedAt).map((day) => (
          <section key={day.label} className="story-day" aria-label={day.label}>
            <h3 className="day">{day.label}</h3>
            <ul className="story">
              {day.groups.map((g) => (
                <li key={g.items[0]?.id ?? g.title} className="story-row">
                  <time className="when" dateTime={new Date(g.last).toISOString()}>
                    {g.items.length > 1 ? `${shortClock(g.first)}–${shortClock(g.last)}` : shortClock(g.last)}
                  </time>
                  <span className={`gly ${g.kind}`} aria-hidden="true" />
                  <div>
                    <b>
                      {g.problemId ? (
                        <Link to={`/problems/${g.problemId}`} className="quiet">
                          {g.items.length > 1 ? `${g.title}, ${g.items.length} times` : g.title}
                        </Link>
                      ) : g.items.length > 1 ? (
                        `${g.title}, ${g.items.length} times`
                      ) : (
                        g.title
                      )}
                    </b>
                    <p className="muted">{g.items.length > 1 ? `Latest: ${g.detail}` : g.detail}</p>
                    {g.items.length > 1 && (
                      <div className="ticks">
                        {g.items.map((i) => (
                          <span key={i.id}>{shortClock(i.at)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {g.kind === "fixed" && groupProblemIds(g).length > 0 ? (
                    <FixedOutcome problemIds={groupProblemIds(g)} />
                  ) : (
                    <span className="outcome">{outcomeLabel(g.kind, null)}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      {hiddenThisWeek(total, h.feed.length) > 0 && (
        // The feed holds the latest problems only; say how many more there are so the count and list agree (G1).
        <p className="muted" style={{ marginTop: "var(--s3)" }}>
          {hiddenThisWeek(total, h.feed.length)} more this week · <Link to="/problems">See all</Link>
        </p>
      )}
      <p className="h3">
        <Link to="/weekly">Weekly report</Link>
      </p>
    </>
  );
}

/** Problems counted in the week's total that the feed didn't send (it holds the latest ones only). */
export function hiddenThisWeek(total: number, inFeed: number): number {
  return Math.max(0, total - inFeed);
}

/** How a week row is shown: the server's outcome, except that an open problem needs you (bug a). */
export type RowKind = FeedItem["kind"] | "open";

/** Did the helper itself fix it? Only a fix it ran with nobody's yes counts; a hand close does not (bug c). */
export function helperFixed(view: ProblemView): boolean {
  return view.actions.some((a) => a.state === "done" && a.approval === null);
}

/** The word at the end of a week row. `helperDidIt` is null when unknown (no problem to check, or still loading). */
export function outcomeLabel(kind: RowKind, helperDidIt: boolean | null): string {
  switch (kind) {
    case "open":
      return "needs you";
    case "needed":
      return "needed you";
    case "told":
      return "told you";
    case "fixed":
      return helperDidIt === true ? "handled alone" : helperDidIt === false ? "closed" : "fixed";
  }
}

/**
 * A grouped "fixed" row speaks for every problem in it, never just the first (bug j):
 * all helper fixes → "handled alone"; none → "closed"; a mix says both counts.
 */
export function groupOutcome(helperDid: boolean[]): string {
  const alone = helperDid.filter(Boolean).length;
  const closed = helperDid.length - alone;
  if (closed === 0) return outcomeLabel("fixed", true);
  if (alone === 0) return outcomeLabel("fixed", false);
  return `${alone} alone, ${closed} closed`;
}

/** The distinct problems behind a row. */
function groupProblemIds(g: FeedGroup): string[] {
  return [...new Set(g.items.map((i) => i.problemId).filter((id): id is string => id !== null))];
}

/** The server marks any resolved problem nobody touched as "fixed"; check the fix log before saying "handled alone". */
function FixedOutcome({ problemIds }: { problemIds: string[] }) {
  const views = useResource((a) => Promise.all(problemIds.map((id) => a.problem(id))), problemIds.join(","));
  return (
    <span className="outcome">
      {views.status === "ok" ? groupOutcome(views.data.map(helperFixed)) : views.status === "error" ? "fixed" : ""}
    </span>
  );
}

type FeedGroup = {
  kind: RowKind;
  title: string;
  detail: string;
  problemId: string | null;
  first: number;
  last: number;
  items: Array<{ id: string; at: number; problemId: string | null }>;
};

/**
 * Tell the story, don't print the log (design rule 5): per day, consecutive items with the same title and
 * outcome collapse into one line with a count and their times. Newest day first; within a day newest first.
 */
export function groupFeed(
  feed: FeedItem[],
  openIds: ReadonlySet<string> = new Set(),
  /** When each problem opened; a row is placed at that time when known. */
  openedAt: ReadonlyMap<string, number> = new Map(),
): Array<{ label: string; groups: FeedGroup[] }> {
  const sorted = [...feed]
    .map((f): Omit<FeedItem, "kind"> & { kind: RowKind } => ({
      ...f,
      at: (f.problemId !== null ? openedAt.get(f.problemId) : undefined) ?? f.at,
      kind: f.kind !== "needed" && f.problemId !== null && openIds.has(f.problemId) ? "open" : f.kind,
    }))
    .sort((a, b) => b.at - a.at);
  const days: Array<{ label: string; groups: FeedGroup[] }> = [];
  for (const f of sorted) {
    const label = new Date(f.at).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
    let day = days.at(-1);
    if (!day || day.label !== label) {
      day = { label, groups: [] };
      days.push(day);
    }
    const g = day.groups.at(-1);
    if (g && g.title === f.title && g.kind === f.kind) {
      g.items.push(f);
      g.first = Math.min(g.first, f.at);
    } else {
      day.groups.push({
        kind: f.kind,
        title: f.title,
        detail: f.detail,
        problemId: f.problemId,
        first: f.at,
        last: f.at,
        items: [f],
      });
    }
  }
  for (const d of days) for (const g of d.groups) g.items.sort((a, b) => a.at - b.at);
  return days;
}

/** The decision on the card (design rule 4): what's wrong, what the helper wants, and the yes right here. */
function NeedsYouCard({ problem, more, onDone }: { problem: Problem; more: number; onDone: () => void }) {
  const view = useResource((a) => a.problem(problem.id), problem.id);
  const pending =
    view.status === "ok" ? (view.data.actions.find((a) => a.state === "awaiting_approval") ?? null) : null;
  const d = problem.diagnosis;
  return (
    <section className="needs" aria-labelledby="needs-h">
      <p className="eyebrow">
        <i aria-hidden="true" />
        Needs you · since {clock(problem.openedAt)}
        {more > 0 ? ` · +${more} more` : ""}
      </p>
      {/* The title always opens the problem, with or without the yes/no below (bug b). */}
      <h2 id="needs-h" className="needs-title">
        <Link to={`/problems/${problem.id}`} className="quiet">
          {problem.title}
        </Link>
      </h2>
      <p className="muted">{d?.what ?? `${problem.eventCount.toLocaleString("en-US")} errors so far.`}</p>
      {pending ? (
        <AskBox action={pending} problemId={problem.id} onDone={onDone} />
      ) : (
        // No yes/no to give: say why it needs you (same words as Problems) and the one way forward (C2).
        <div className="head-act">
          {view.status === "ok" && (
            <p style={{ margin: "0 0 var(--s3)" }}>
              <b>{openLine(view.data)}</b>
            </p>
          )}
          <Link to={`/problems/${problem.id}`} className="btn hot sm">
            Open
          </Link>
        </div>
      )}
    </section>
  );
}

const STATE_LABEL: Record<Problem["state"], { text: string; cls: string }> = {
  open: { text: "Needs you", cls: "st hot" },
  acked: { text: "You're on it", cls: "st" },
  fixing: { text: "Fixing", cls: "st" },
  resolved: { text: "Fixed", cls: "st ok" },
};

/** Open first, then the rest; newest first within each. */
const STATE_ORDER: Record<Problem["state"], number> = { open: 0, acked: 1, fixing: 1, resolved: 2 };

export function Problems() {
  const { now } = useDeps();
  const problems = useResource((a) => a.problems());
  if (problems.status === "loading") return <Loading what="Loading problems" />;
  if (problems.status === "error") return <ErrorNote error={problems.error} retry={problems.reload} />;
  const list = [...problems.data].sort(
    (a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || b.openedAt - a.openedAt,
  );
  return (
    <>
      <title>Problems · Pagerless</title>
      <ScreenHead pager={problemsPager(list, now())} title="Problems" />
      {list.length === 0 ? (
        <p className="muted">No problems yet.</p>
      ) : (
        <ul className="box list">
          {list.map((p) => (
            <ProblemRow key={p.id} problem={p} />
          ))}
        </ul>
      )}
    </>
  );
}

/** Today's closed problems, split by who fixed them: your yes, or the helper alone. Hand closes count for neither. */
export function whoFixedToday(views: ProblemView[]): { youToday: number; helperToday: number } {
  let youToday = 0;
  let helperToday = 0;
  for (const v of views) {
    const how = endedHow(v);
    if (how.line === WITH_YOUR_YES) youToday += 1;
    else if (how.line === HELPER_ALONE) helperToday += 1;
  }
  return { youToday, helperToday };
}

const WITH_YOUR_YES = "Fixed with your yes";
const HELPER_ALONE = "Your helper fixed it alone";

/** How a problem ended, from its fix log. Only a fix that ran counts as fixed; a hand close is "Closed" (bug 1). */
export function endedHow(view: ProblemView): { badge: string; cls: string; line: string } {
  if (view.actions.some((a) => a.state === "done" && a.approval !== null))
    return { badge: "Fixed", cls: "st ok", line: WITH_YOUR_YES };
  if (helperFixed(view)) return { badge: "Fixed", cls: "st ok", line: HELPER_ALONE };
  return { badge: "Closed", cls: "st", line: view.problem.summary ?? "Closed by hand" };
}

/** What an open problem asks of you, in plain words: the pending yes/no, else what it's doing (bug 2). */
export function openLine(view: ProblemView): string {
  const ask = view.actions.find((a) => a.state === "awaiting_approval");
  if (ask) return `${ask.title}?`;
  if (view.actions.some((a) => a.state === "failed")) return "The fix failed. Needs you.";
  if (view.needsYou) return "We paged you. Needs you.";
  if (view.actions.some((a) => ACTIVE_STATES.includes(a.state))) return "Fixing now";
  return view.pagedAt !== null ? "We paged you" : "Your helper is looking into it";
}

const ACTIVE_STATES: ProblemView["actions"][number]["state"][] = ["approved", "running", "verifying"];

/** One problem: a status, its title, one line that differs per row (the ask, or how it ended), and when it started. */
function ProblemRow({ problem: p }: { problem: Problem }) {
  const view = useResource((a) => a.problem(p.id), p.id);
  const ended = p.state === "resolved" && view.status === "ok" ? endedHow(view.data) : null;
  const badge = ended ?? STATE_LABEL[p.state];
  const line =
    view.status !== "ok"
      ? null
      : p.state === "resolved"
        ? (ended?.line ?? null)
        : p.state === "open"
          ? openLine(view.data)
          : null;
  return (
    <li className="row feed">
      <span className={ended ? ended.cls : badge.cls}>{ended ? ended.badge : STATE_LABEL[p.state].text}</span>
      <div>
        <b>
          <Link to={`/problems/${p.id}`}>{p.title}</Link>
        </b>
        {p.service && <span className="chip">{serviceLabel(p.service)}</span>}
        {line && <p className="muted">{line}</p>}
      </div>
      <time className="when" dateTime={new Date(p.openedAt).toISOString()}>
        {dayClock(p.openedAt)}
      </time>
    </li>
  );
}

/** One line per app. Open problems come first: an app with one is failing, whatever its heartbeat says. */
export function appLine(p: Project, nowMs: number, openProblems: number): string {
  const host = p.host ? ` · ${HOST_NAME[p.host] ?? p.host}` : "";
  if (openProblems > 0) return `Failing: ${openProblems} problem${openProblems === 1 ? "" : "s"} open${host}`;
  switch (p.state) {
    case "up": {
      const days = p.since === null ? null : Math.max(0, Math.floor((nowMs - p.since) / 86_400_000));
      return `${days === null ? "Up" : days === 0 ? "Up today" : `Up ${days} day${days === 1 ? "" : "s"}`}${host}`;
    }
    case "down":
      return `Down${host}`;
    case "paused":
      return "Paused by you";
    case "waiting":
      return `No signal yet${host}`;
  }
}

export function Apps() {
  const { now } = useDeps();
  const projects = useResource((a) => a.projects());
  const open = useResource((a) => a.problems("open"));
  if (projects.status === "loading" || open.status === "loading") return <Loading what="Loading your apps" />;
  if (projects.status === "error") return <ErrorNote error={projects.error} retry={projects.reload} />;
  if (open.status === "error") return <ErrorNote error={open.error} retry={open.reload} />;
  const openFor = (id: string) => open.data.filter((o) => o.projectId === id).length;
  return (
    <>
      <title>Apps · Pagerless</title>
      <ScreenHead pager={appsPager(projects.data, open.data)} title="Apps">
        <Link to="/setup" className="btn alt sm">
          Add an app
        </Link>
      </ScreenHead>
      <ul className="box list">
        {projects.data.map((p) => (
          <Fragment key={p.id}>
            <li className="row">
              <span
                className={`dot${p.state === "down" || openFor(p.id) > 0 ? " down" : p.state === "up" ? " up" : ""}`}
                aria-hidden="true"
              />
              <div>
                <b>
                  <Link to={appHref(p.id)}>{p.name}</Link>
                </b>
                <p className="muted">{appLine(p, now(), openFor(p.id))}</p>
              </div>
              <Link
                to={`/apps/${encodeURIComponent(p.id)}/tools`}
                className="btn alt sm"
                aria-label={`Other tools for ${p.name}`}
              >
                Other tools
              </Link>
            </li>
            <Jobs projectId={p.id} />
          </Fragment>
        ))}
      </ul>
    </>
  );
}

/** "Phone, Call: OK today" · "Email failed. Fix it now." · "Not tested yet" */
export function lastTestLine(channels: Channel[], nowMs: number): string {
  const tested = channels.filter((c) => c.levels.length > 0 && c.lastTestAt !== null);
  if (tested.length === 0) return "Not tested yet";
  const failed = tested.filter((c) => c.lastTestOk === false);
  if (failed.length > 0) return `${failed.map((c) => CHANNEL_NAME[c.kind]).join(", ")} failed. Fix it now.`;
  const names = [...new Set(tested.map((c) => CHANNEL_NAME[c.kind]))];
  const latest = Math.max(...tested.map((c) => c.lastTestAt ?? 0));
  const today = new Date(latest).toDateString() === new Date(nowMs).toDateString();
  return `${names.join(", ")}: OK ${today ? "today" : new Date(latest).toDateString()}`;
}

export function Alerts() {
  const { api, now } = useDeps();
  const alerts = useResource((a) => a.alerts());
  const channels = useResource((a) => a.channels());
  const [test, setTest] = useState<{ s: "idle" | "sending" | "done" } | { s: "error"; error: Error }>({ s: "idle" });

  async function sendTest() {
    if (channels.status !== "ok") return;
    setTest({ s: "sending" });
    try {
      const active = channels.data.filter((c) => c.levels.length > 0);
      const results = await Promise.all(active.map((c) => api.testChannel(c.id)));
      channels.reload();
      const bad = results.filter((r) => !r.ok);
      if (bad.length > 0) throw new Error(`${bad.length} channel${bad.length === 1 ? "" : "s"} failed the test.`);
      setTest({ s: "done" });
    } catch (e) {
      // A failed alert path must be loud.
      setTest({ s: "error", error: e instanceof Error ? e : new Error(String(e)) });
      channels.reload();
    }
  }

  if (alerts.status === "loading" || channels.status === "loading") return <Loading what="Loading alerts" />;
  if (alerts.status === "error") return <ErrorNote error={alerts.error} retry={alerts.reload} />;
  if (channels.status === "error") return <ErrorNote error={channels.error} retry={channels.reload} />;
  const a = alerts.data;
  return (
    <>
      <title>Alerts · Pagerless</title>
      <ScreenHead pager={alertsPager(channels.data, now())} title="Alerts">
        <button type="button" className="btn alt sm" onClick={sendTest} disabled={test.s === "sending"}>
          {test.s === "sending" ? "Sending…" : "Send a test"}
        </button>
        <div aria-live="polite">
          {test.s === "done" && <p className="ok-msg">Test sent. Check your phone.</p>}
          {test.s === "error" && <ErrorNote error={test.error} />}
        </div>
      </ScreenHead>
      <ul className="box list">
        <li className="row two">
          <div>
            <b>Wake me for</b>
            <p className="muted">{a.wakeFor.length > 0 ? a.wakeFor.join(", ") : "Only what your helper can't fix"}</p>
          </div>
        </li>
        <li className="row two">
          <div>
            <b>Quiet hours</b>
            <p className="muted">{a.quietHours ? `${a.quietHours.from}–${a.quietHours.to} · wake-me only` : "Off"}</p>
          </div>
        </li>
        <li className="row two">
          <div>
            <b>Morning summary</b>
            <p className="muted">{a.summary ? `${a.summary.at} · ${a.summary.where}` : "Off"}</p>
          </div>
        </li>
        <li className="row two">
          <div>
            <b>Last test</b>
            <p className="muted">{lastTestLine(channels.data, now())}</p>
          </div>
        </li>
      </ul>
      <h2 className="h3">Where alerts go</h2>
      <ChannelRows channels={channels.data} reload={channels.reload} />
    </>
  );
}

/** "Wakes and tells · OK today" · "Tells · failed last test" · "Off" */
export function channelLine(c: Channel, nowMs: number): string {
  if (c.levels.length === 0) return "Off";
  const what =
    c.levels.includes("wake") && c.levels.includes("tell")
      ? "Wakes and tells"
      : c.levels.includes("wake")
        ? "Wakes"
        : "Tells";
  const test =
    c.lastTestAt === null
      ? "not tested"
      : c.lastTestOk === false
        ? "failed last test"
        : new Date(c.lastTestAt).toDateString() === new Date(nowMs).toDateString()
          ? "OK today"
          : `OK ${new Date(c.lastTestAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  return `${what} · ${test}`;
}

/** Every channel we can send to, per person, each with its own switch: the pager's count is this list (bug 5). */
function ChannelRows({ channels, reload }: { channels: Channel[]; reload: () => void }) {
  const { api, now } = useDeps();
  const people = useResource((a) => a.people());
  const [err, setErr] = useState<Error | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const nameOf = (id: string | null) =>
    id === null ? "Everyone" : people.status === "ok" ? (people.data.find((p) => p.id === id)?.name ?? "Someone") : "";
  async function toggle(c: Channel, on: boolean) {
    setErr(null);
    setBusy(c.id);
    try {
      await api.saveChannel({ ...c, levels: on ? defaultLevels(c.kind) : [] });
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(null);
    }
  }
  return (
    <>
      <ul className="box list">
        {channels.map((c) => {
          const id = `chn-${c.id}`;
          const label = `${CHANNEL_NAME[c.kind]} · ${nameOf(c.personId)}`;
          return (
            <li key={c.id} className="row two">
              <label className="check" htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={c.levels.length > 0}
                  disabled={busy === c.id}
                  onChange={(e) => void toggle(c, e.target.checked)}
                />
                <span>
                  <b>{label}</b>
                  <span
                    className={`muted${c.lastTestOk === false && c.levels.length > 0 ? " bad" : ""}`}
                    style={{ display: "block" }}
                  >
                    {channelLine(c, now())}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {err && <ErrorNote error={err} />}
    </>
  );
}

export function People() {
  const people = useResource((a) => a.people());
  return (
    <>
      <title>People · Pagerless</title>
      <ScreenHead
        pager={people.status === "ok" ? peoplePager(people.data) : LOADING_PAGER}
        title="Who should we wake?"
      />
      <PeopleList />
      {/* How we reach people lives in one place: Alerts (bug 6). */}
      <p className="note" style={{ marginTop: "var(--s4)" }}>
        How we reach them: <Link to="/alerts">Alerts</Link>
      </p>
    </>
  );
}

export function Plan() {
  const { api, redirect } = useDeps();
  const plan = useResource((a) => a.plan());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function upgrade() {
    setBusy(true);
    setError(null);
    try {
      redirect((await api.checkout()).url);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setBusy(false);
    }
  }

  if (plan.status === "loading") return <Loading what="Loading your plan" />;
  if (plan.status === "error") return <ErrorNote error={plan.error} retry={plan.reload} />;
  const p = plan.data;
  return (
    <>
      <title>Plan · Pagerless</title>
      <ScreenHead
        pager={planPager(p)}
        title="Plan"
        sub={`${p.people.used}${p.people.limit === null ? "" : ` of ${p.people.limit}`} ${p.people.used === 1 ? "person" : "people"} · ${p.apps} app${p.apps === 1 ? "" : "s"}`}
      >
        {p.plan === "free" && (
          <button type="button" className="btn sm" onClick={upgrade} disabled={busy}>
            {busy ? "Opening checkout…" : "Upgrade"}
          </button>
        )}
        {error && <ErrorNote error={error} />}
      </ScreenHead>
      <div className="two peers">
        <div className={`box planc${p.plan === "free" ? " on" : ""}`}>
          <b>Free</b>
          <p className="muted">You + 1 backup · 3 apps · unlimited fixes</p>
          {p.plan === "free" && <span className="st ok">Your plan</span>}
        </div>
        <div className={`box planc${p.plan === "team" ? " on" : ""}`}>
          <b>Team</b>
          <p className="muted">More people · pay per alert · no seat price</p>
          {p.plan === "team" && <span className="st ok">Your plan</span>}
        </div>
      </div>
      <p className="note">Calls use your own Twilio. No markup.</p>
    </>
  );
}

/** Screen 12: the weekly report, one pager. */
/** "15–22 Oct": the week the report covers, ending today. */
export function weekRange(nowMs: number): string {
  const end = new Date(nowMs);
  const start = new Date(nowMs - 7 * 86_400_000);
  const day = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric" });
  const month = (d: Date) => d.toLocaleDateString("en-GB", { month: "short" });
  return month(start) === month(end)
    ? `${day(start)}–${day(end)} ${month(end)}`
    : `${day(start)} ${month(start)} – ${day(end)} ${month(end)}`;
}

/** Screen 12: the weekly report. The pager is the report; the dates and time-to-answer sit under the title. */
export function Weekly() {
  const { now } = useDeps();
  const weekly = useResource((a) => a.weekly());
  if (weekly.status === "loading") return <Loading what="Loading this week" />;
  if (weekly.status === "error") return <ErrorNote error={weekly.error} retry={weekly.reload} />;
  const w = weekly.data;
  const neededYou = w.neededYou ?? w.needed;
  const pager = weeklyPager({ fixedByHelper: w.fixedByHelper ?? w.fixed, neededYou });
  return (
    <>
      <title>This week · Pagerless</title>
      <ScreenHead
        pager={pager}
        title="This week"
        sub={`${weekRange(now())}${
          neededYou > 0 ? ` · ${lcdDuration(w.secondsSpent * 1000).toLowerCase()} from page to your answer` : ""
        }`}
      />
    </>
  );
}
